import { createContext, useContext, useMemo } from 'react';

import { ACCOUNT_STATUS } from '#client/constants/account';
import { MISSING_PAYMENT_EXPIRED_DAY, SUBSCRIPTION_STATUS } from '#client/constants/subscription';
import useApplicationStatusContext from '#client/hooks/useApplicationStatusContext';
import useCheckOpenModal from '#client/hooks/useCheckOpenModals';
import useUnitAccount from '#client/hooks/useUnitAccount';
import useUnitAccountId from '#client/hooks/useUnitAccountId';
import { getDiffTime } from '#client/utils/date';

import useSubScriptionContext from './useSubscriptionContext';

export const DisableDashboardContext = createContext();
DisableDashboardContext.displayName = 'DisableDashboardContext';

const useDisableDashboardContext = () => {
  const context = useContext(DisableDashboardContext);

  if (typeof context === 'undefined') {
    throw new Error('useDisableDashboard must be used within a DisableDashboardContext.Provider');
  }

  return context;
};

export function DisableDashboardProvider({ children }) {
  const { unitAccountId, isValidMfa, isEnrolled, isEnabled } = useUnitAccountId();
  const { isApprovedStatus } = useApplicationStatusContext();
  const { subscriptionStatus, isMissedPayment, missingPaymentDate } = useSubScriptionContext();
  const { unitAccount, loading: loadingUnitAccount } = useUnitAccount();

  const { isOpeningModal } = useCheckOpenModal();

  const isExpiredMissingPayment =
    subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE &&
    isMissedPayment &&
    Math.abs(getDiffTime(missingPaymentDate)) > MISSING_PAYMENT_EXPIRED_DAY;

  const value = useMemo(() => {
    // Disable dashboard when MFA is invalid
    const isDisable =
      !isEnrolled ||
      !isEnabled ||
      !isValidMfa ||
      !unitAccountId ||
      !isApprovedStatus ||
      isExpiredMissingPayment ||
      isOpeningModal ||
      loadingUnitAccount ||
      unitAccount?.status === ACCOUNT_STATUS.FROZEN;

    return {
      isDisable
    };
  }, [
    isApprovedStatus,
    isEnabled,
    isEnrolled,
    isOpeningModal,
    isValidMfa,
    unitAccount?.status,
    unitAccountId,
    isExpiredMissingPayment,
    loadingUnitAccount
  ]);

  return (
    <DisableDashboardContext.Provider value={value}>{children}</DisableDashboardContext.Provider>
  );
}

export default useDisableDashboardContext;
